import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders, handleUnauthorized } from "../../utils/api";
import YearlyCalendarPage from "../shared/calendar/YearlyCalendarPage";
import ParentProfile from "./ParentProfile";
import ParentSubjectDetail from "./ParentSubjectDetail";
import ParentSubjectAttendance from "./ParentSubjectAttendance";
import ParentChildTimetable from "./ParentChildTimetable";
import ParentFees from "./ParentFees";
import ParentNotifications from "./ParentNotifications";
import "./parent.css";

const ACTIVE_CHILD_KEY = "als_parent_active_child_id";

function getActiveChildId() {
  return sessionStorage.getItem(ACTIVE_CHILD_KEY);
}

function setActiveChildId(id) {
  if (id) sessionStorage.setItem(ACTIVE_CHILD_KEY, String(id));
  else sessionStorage.removeItem(ACTIVE_CHILD_KEY);
}

function ParentShell({ children, childrenList, parentData }) {
  const navigate = useNavigate();
  const location = useLocation();
  const activeId = getActiveChildId();
  const activeChild = childrenList.find((c) => String(c.id) === String(activeId));
  const showSwitcher =
    childrenList.length > 1 &&
    !location.pathname.includes("/select-child") &&
    !location.pathname.includes("/profile");

  return (
    <div className="parent-portal">
      {showSwitcher && (
        <div className="pp-child-switcher">
          <span>Viewing:</span>
          <strong>{activeChild?.name || "Select a child"}</strong>
          <button
            type="button"
            className="pp-btn pp-btn--ghost pp-btn--sm"
            onClick={() => navigate("/parent/select-child")}
          >
            Switch child
          </button>
        </div>
      )}
      {children}
    </div>
  );
}

function ParentSelectChild({ childrenList, loading }) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <p>Loading children…</p>
      </div>
    );
  }

  if (childrenList.length === 0) {
    return (
      <div className="pp-empty pp-animate-in">
        <p>No linked students found for your CNIC.</p>
      </div>
    );
  }

  return (
    <div className="pp-page pp-animate-in">
      <header className="pp-hero">
        <div>
          <p className="pp-kicker">Parent Portal</p>
          <h1>My Children</h1>
          <p className="pp-sub">
            {childrenList.length > 1
              ? "Siblings share one parent account. Choose which child\u2019s activity and records you want to view."
              : "Open your child\u2019s profile to view attendance, subjects, and school updates."}
          </p>
        </div>
        <div className="pp-hero-badge">
          <span>{childrenList.length}</span>
          <small>Child{childrenList.length === 1 ? "" : "ren"}</small>
        </div>
      </header>

      <div className="pp-child-grid">
        {childrenList.map((child, index) => (
          <button
            key={child.id}
            type="button"
            className="pp-child-card"
            style={{ animationDelay: `${0.08 + index * 0.06}s` }}
            onClick={() => {
              setActiveChildId(child.id);
              navigate(`/parent/children/${child.id}`);
            }}
          >
            <div className="pp-child-card__top">
              <span className="pp-avatar">{(child.name || "?").charAt(0)}</span>
              <span className="pp-chip">{child.student_status || "Active"}</span>
            </div>
            <h3>{child.name}</h3>
            <p className="pp-mono">{child.student_id}</p>
            <div className="pp-child-meta">
              <span>
                {child.class_name || "Class"}
                {child.class_section ? ` · ${child.class_section}` : ""}
              </span>
              <span>{child.campus_name || "Campus"}</span>
            </div>
            <span className="pp-link">View this child →</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ParentOverview({ parentData, childrenList, loading }) {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/parents/me/analytics`, {
          headers: getAuthHeaders(),
        });
        if (res.status === 401) {
          handleUnauthorized();
          return;
        }
        if (res.ok && alive) setAnalytics(await res.json());
      } catch (err) {
        console.error(err);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <p>Loading parent portal…</p>
      </div>
    );
  }

  return (
    <div className="pp-page pp-animate-in">
      <header className="pp-hero">
        <div>
          <p className="pp-kicker">Parent Portal</p>
          <h1>Welcome{parentData?.name ? `, ${parentData.name}` : ""}</h1>
          <p className="pp-sub">
            View-only access to your child&apos;s class, subjects, teachers, attendance, and school calendar.
          </p>
        </div>
        <div className="pp-hero-badge">
          <span>{childrenList.length}</span>
          <small>Child{childrenList.length === 1 ? "" : "ren"}</small>
        </div>
      </header>

      <section className="pp-stats">
        <article className="pp-stat-card pp-stat-card--teal">
          <h3>{analytics?.overall_attendance_rate ?? 0}%</h3>
          <p>Overall attendance</p>
        </article>
        <article className="pp-stat-card pp-stat-card--green">
          <h3>{analytics?.total_present ?? 0}</h3>
          <p>Days present</p>
        </article>
        <article className="pp-stat-card pp-stat-card--rose">
          <h3>{analytics?.total_absent ?? 0}</h3>
          <p>Days absent</p>
        </article>
        <article className="pp-stat-card pp-stat-card--gold">
          <h3>{analytics?.total_marked ?? 0}</h3>
          <p>Records marked</p>
        </article>
      </section>

      <h2 className="pp-section-title">Your children</h2>
      <div className="pp-child-grid">
        {childrenList.length === 0 ? (
          <div className="pp-empty">No linked students found for your CNIC.</div>
        ) : (
          childrenList.map((child, index) => (
            <button
              key={child.id}
              type="button"
              className="pp-child-card"
              style={{ animationDelay: `${0.1 + index * 0.06}s` }}
              onClick={() => {
                setActiveChildId(child.id);
                navigate(`/parent/children/${child.id}`);
              }}
            >
              <div className="pp-child-card__top">
                <span className="pp-avatar">{(child.name || "?").charAt(0)}</span>
                <span className="pp-chip">{child.student_status || "Active"}</span>
              </div>
              <h3>{child.name}</h3>
              <p className="pp-mono">{child.student_id}</p>
              <div className="pp-child-meta">
                <span>{child.class_name || "Class"}{child.class_section ? ` · ${child.class_section}` : ""}</span>
                <span>{child.campus_name || "Campus"}</span>
              </div>
              <span className="pp-link">View details →</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function ParentChildDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (studentId) setActiveChildId(studentId);
  }, [studentId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/parents/me/children/${studentId}/overview`,
          { headers: getAuthHeaders() },
        );
        if (res.status === 401) {
          handleUnauthorized();
          return;
        }
        if (!res.ok) throw new Error("Failed to load student");
        if (alive) setOverview(await res.json());
      } catch (err) {
        console.error(err);
        if (alive) setOverview(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [studentId]);

  if (loading) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <p>Loading student details…</p>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="pp-empty">
        <p>Student details unavailable.</p>
        <button type="button" className="pp-btn pp-btn--ghost pp-btn--icon" aria-label="Back" onClick={() => navigate("/parent/select-child")}>
          ←
        </button>
      </div>
    );
  }

  const att = overview.attendance || {};

  return (
    <div className="pp-page pp-animate-in">
      <div className="pp-detail-top">
        <button type="button" className="pp-btn pp-btn--ghost pp-btn--icon" aria-label="Back" onClick={() => navigate("/parent/select-child")}>
          ←
        </button>
        <div>
          <h1>{overview.name}</h1>
          <p>
            <span className="pp-mono">{overview.student_id}</span>
            {" · "}
            {overview.class_name || "Class"}
            {overview.class_section ? ` · Section ${overview.class_section}` : ""}
            {overview.campus_name ? ` · ${overview.campus_name}` : ""}
          </p>
        </div>
      </div>

      <section className="pp-info-grid">
        <article className="pp-panel">
          <h2>Profile</h2>
          <div className="pp-kv"><span>Session</span><strong>{overview.academic_session || "—"}</strong></div>
          <div className="pp-kv"><span>Status</span><strong>{overview.student_status || "—"}</strong></div>
          <div className="pp-kv"><span>Gender</span><strong>{overview.gender || "—"}</strong></div>
          <div className="pp-kv"><span>Admission</span><strong>{overview.admission_date || "—"}</strong></div>
          <div className="pp-kv"><span>Father</span><strong>{overview.father_name || "—"}</strong></div>
        </article>

        <article className="pp-panel">
          <h2>Attendance analytics</h2>
          <div className="pp-att-grid">
            <div><strong>{att.attendance_rate || 0}%</strong><span>Rate</span></div>
            <div><strong>{att.present || 0}</strong><span>Present</span></div>
            <div><strong>{att.absent || 0}</strong><span>Absent</span></div>
            <div><strong>{att.late || 0}</strong><span>Late</span></div>
            <div><strong>{att.leave || 0}</strong><span>Leave</span></div>
            <div><strong>{att.total || 0}</strong><span>Total</span></div>
          </div>
        </article>
      </section>

      <section className="pp-panel pp-subjects-panel">
        <div className="pp-subjects-head">
          <div>
            <h2>Subjects &amp; teachers</h2>
            <p className="pp-muted">Tap a subject to view attendance, remarks, homework, and uploaded class files.</p>
          </div>
          <button
            type="button"
            className="pp-btn pp-btn--primary pp-btn--sm"
            onClick={() => navigate(`/parent/children/${studentId}/timetable`)}
          >
            View timetable
          </button>
        </div>
        {overview.subjects?.length ? (
          <div className="pp-subject-grid">
            {overview.subjects.map((s) => (
              <button
                key={`${s.course_id}-${s.teacher_id || ""}`}
                type="button"
                className="pp-subject-card pp-subject-card--clickable"
                onClick={() => navigate(`/parent/children/${studentId}/subjects/${s.course_id}`)}
              >
                <strong>{s.course_name}</strong>
                <span>{s.teacher_name || "Teacher TBD"}</span>
                {s.section ? <small>Section {s.section}</small> : null}
                <span className="pp-link">Open subject →</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="pp-muted">No subject assignments found for this class yet.</p>
        )}
      </section>
    </div>
  );
}

function ParentTimetableRedirect({ childrenList, loading }) {
  const navigate = useNavigate();
  const activeId = getActiveChildId();

  useEffect(() => {
    if (loading) return;
    if (activeId) {
      navigate(`/parent/children/${activeId}/timetable`, { replace: true });
      return;
    }
    if (childrenList.length === 1) {
      navigate(`/parent/children/${childrenList[0].id}/timetable`, { replace: true });
      return;
    }
    navigate("/parent/select-child", { replace: true });
  }, [activeId, childrenList, loading, navigate]);

  return (
    <div className="pp-loading">
      <div className="pp-spinner" />
      <p>Opening timetable…</p>
    </div>
  );
}

function ParentAnalytics({ childrenList }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const activeChildId = getActiveChildId();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const qs = activeChildId ? `?student_id=${encodeURIComponent(activeChildId)}` : "";
        const res = await fetch(`${API_BASE_URL}/api/parents/me/analytics${qs}`, {
          headers: getAuthHeaders(),
        });
        if (res.ok && alive) setAnalytics(await res.json());
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [activeChildId]);

  if (loading) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <p>Loading analytics…</p>
      </div>
    );
  }

  return (
    <div className="pp-page pp-animate-in">
      <header className="pp-page-header">
       
      </header>
      <section className="pp-stats">
        <article className="pp-stat-card pp-stat-card--teal">
          <h3>{analytics?.children_count ?? childrenList.length}</h3>
          <p>Children</p>
        </article>
        <article className="pp-stat-card pp-stat-card--green">
          <h3>{analytics?.overall_attendance_rate ?? 0}%</h3>
          <p>Attendance rate</p>
        </article>
        <article className="pp-stat-card pp-stat-card--gold">
          <h3>{analytics?.total_present ?? 0}</h3>
          <p>Present</p>
        </article>
        <article className="pp-stat-card pp-stat-card--rose">
          <h3>{analytics?.total_absent ?? 0}</h3>
          <p>Absent</p>
        </article>
      </section>
      <div className="pp-panel">
        <h2>Per child</h2>
        <div className="pp-table-wrap">
          <table className="pp-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Class</th>
                <th>Subjects</th>
                <th>Attendance</th>
              </tr>
            </thead>
            <tbody>
              {(analytics?.children || []).map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.name}</strong>
                    <div className="pp-mono">{c.student_id}</div>
                  </td>
                  <td>{c.class_name || "—"}{c.class_section ? ` · ${c.class_section}` : ""}</td>
                  <td>{c.subjects_count}</td>
                  <td>{c.attendance?.attendance_rate ?? 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ParentDashboard({ onLogout }) {
  const [parentData, setParentData] = useState(null);
  const [childrenList, setChildrenList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [meRes, kidsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/parents/me`, { headers: getAuthHeaders() }),
          fetch(`${API_BASE_URL}/api/parents/me/children`, { headers: getAuthHeaders() }),
        ]);
        if (meRes.status === 401 || kidsRes.status === 401) {
          handleUnauthorized(onLogout);
          return;
        }
        if (meRes.ok && alive) {
          const me = await meRes.json();
          setParentData(me);
          localStorage.setItem("als_parent_name", me.name || "");
        }
        if (kidsRes.ok && alive) {
          const kids = await kidsRes.json();
          setChildrenList(Array.isArray(kids) ? kids : []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [onLogout]);

  return (
    <ParentShell childrenList={childrenList} parentData={parentData}>
      <Routes>
        <Route
          path="select-child"
          element={<ParentSelectChild childrenList={childrenList} loading={loading} />}
        />
        <Route
          path="overview"
          element={
            <ParentOverview
              parentData={parentData}
              childrenList={childrenList}
              loading={loading}
            />
          }
        />
        <Route path="children/:studentId" element={<ParentChildDetail />} />
        <Route path="children/:studentId/subjects/:courseId" element={<ParentSubjectDetail />} />
        <Route
          path="children/:studentId/subjects/:courseId/attendance"
          element={<ParentSubjectAttendance />}
        />
        <Route path="children/:studentId/timetable" element={<ParentChildTimetable />} />
        <Route
          path="timetable"
          element={<ParentTimetableRedirect childrenList={childrenList} loading={loading} />}
        />
        <Route
          path="analytics"
          element={<ParentAnalytics childrenList={childrenList} />}
        />
        <Route
          path="fees"
          element={
            <ParentFees
              childrenList={childrenList}
              loading={loading}
              onLogout={onLogout}
            />
          }
        />
        <Route
          path="notifications"
          element={<ParentNotifications onLogout={onLogout} />}
        />
        <Route
          path="profile"
          element={
            <ParentProfile
              parentData={parentData}
              onLogout={onLogout}
              onProfileUpdated={(data) => setParentData(data)}
            />
          }
        />
        <Route path="calendar" element={<YearlyCalendarPage variant="teacher" />} />
        <Route path="*" element={<Navigate to="/parent/overview" replace />} />
      </Routes>
    </ParentShell>
  );
}

export default ParentDashboard;
